import React, { useState, useEffect, useRef } from 'react';

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  message: string;
  onConfirm: (reason?: string) => void;
  confirmLabel?: string;
  cancelLabel?: string;
  showReasonInput?: boolean;
  confirmButtonVariant?: 'primary' | 'danger';
  maxReasonLength?: number;
  disabled?: boolean;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  title,
  message,
  onConfirm,
  confirmLabel = 'Yes',
  cancelLabel = 'No',
  showReasonInput = false,
  confirmButtonVariant = 'primary',
  maxReasonLength,
  disabled = false,
}) => {
  const [reason, setReason] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      setReason('');
    }
  }, [open]);

  // Focus management
  useEffect(() => {
    if (open) {
      let attempts = 0;
      let timer: NodeJS.Timeout;

      const tryFocus = () => {
        const targetElement = showReasonInput
          ? textareaRef.current
          : cancelButtonRef.current;

        if (targetElement && attempts < 10) {
          targetElement.focus();
          // Check if focus was successful
          if (document.activeElement === targetElement) {
            return; // Success, stop trying
          }
          attempts++;
          timer = setTimeout(tryFocus, 100);
        }
      };

      // Start trying to focus after a short delay
      timer = setTimeout(tryFocus, 100);

      return () => {
        if (timer) clearTimeout(timer);
      };
    }
  }, [open, showReasonInput]);

  // Handle Escape key
  useEffect(() => {
    if (open) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [open, onClose]);

  if (!open) return null;

  const handleConfirm = () => {
    onConfirm(showReasonInput ? reason : undefined);
  };

  const buttonClass =
    confirmButtonVariant === 'danger'
      ? 'bg-red-600 hover:bg-red-700'
      : 'bg-blue-600 hover:bg-blue-700';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999]">
      <div className="bg-gray-100 p-6 rounded-lg max-w-md w-full mx-4">
        <h1 className="text-lg font-medium mb-2">{title}</h1>
        <p className="mb-4">{message}</p>

        {showReasonInput && (
          <div className="mb-4">
            <label
              htmlFor="rejectionReason"
              aria-label="Reason for rejection"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Reason for rejection
            </label>
            <textarea
              ref={textareaRef}
              id="rejectionReason"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={reason}
              onChange={(e) => {
                const value = e.target.value;
                setReason(
                  typeof maxReasonLength === 'number'
                    ? value.slice(0, maxReasonLength)
                    : value
                );
              }}
              placeholder="Please provide a reason for rejection..."
              maxLength={maxReasonLength}
              required
            />
            {typeof maxReasonLength === 'number' && (
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs text-gray-500">
                  {reason.length}/{maxReasonLength} characters
                </span>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-300"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={disabled || (showReasonInput && !reason.trim())}
            className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${buttonClass} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
